import { withDeadline } from "./deadline.js";
import type { ManifestPlanner } from "./manifest-sync.js";

/** The only seam between browser code and generated Rust/WASM bindings. */

/** A vault cache running in the browser, backed by the Rust core. */
export interface Vault {
  /** Normalizes a vault path, throwing if it is empty or escapes the vault. */
  normalizePath(raw: string): string;
}

/** How the client reaches a compiled core. */
export interface CoreModule {
  Vault: new () => Vault;
  /** One manifest pull; `start` is `{ checkpoint, local: [{ path, etag, unsent }], maxBodyBytes }`. */
  ManifestSync: new (start: string) => ManifestPlanner & { free(): void };
  /** The core's one note-title rule: declared title, else first H1, else filename. */
  documentTitle(body: string, path: string): string;
}

interface GeneratedCoreModule extends Partial<CoreModule> {
  default?: (input?: { readonly module_or_path: URL }) => Promise<unknown>;
}

export interface LoadedCore {
  readonly module: CoreModule;
  readonly mode: "wasm";
}

let loading: Promise<LoadedCore> | undefined;

/**
 * Loads the WASM core once per realm.
 *
 * Production builds look for the generated bundle at `/wasm/rv_wasm.js`.
 * Production fails closed when the generated core cannot load. Running a
 * second implementation here hides packaging regressions and can silently
 * change path semantics.
 *
 * The result is shared: the vault worker and the title rule both need the
 * core, and instantiating it twice in one thread buys nothing. A failed load is
 * not cached, so the next caller gets a fresh attempt.
 */
export function loadCore(): Promise<LoadedCore> {
  loading ??= withDeadline(() => loadGeneratedCore(), 5_000, "RecallVault core loading timed out while importing or initializing WASM. Check the connection and try again.").catch((error: unknown) => {
    loading = undefined;
    throw error;
  });
  return loading;
}

async function loadGeneratedCore(): Promise<LoadedCore> {
  try {
    const wasmUrl = "/wasm/rv_wasm.js";
    const candidate: unknown = await import(
      /* @vite-ignore */ wasmUrl
    );
    const module = candidate as GeneratedCoreModule;
    if (typeof module.default === "function") {
      await module.default({ module_or_path: new URL("/wasm/rv_wasm_bg.wasm", self.location.origin) });
    }
    if (typeof module.Vault !== "function") {
      throw new Error("generated module does not export Vault");
    }
    if (typeof module.documentTitle !== "function") {
      throw new Error("generated module does not export documentTitle");
    }
    if (typeof module.ManifestSync !== "function") {
      throw new Error("generated module does not export ManifestSync");
    }
    return { module: module as CoreModule, mode: "wasm" };
  } catch (error) {
    throw new Error(`RecallVault's secure core could not load: ${error instanceof Error ? error.message : "WASM unavailable"}`);
  }
}
